import type { TabSecurityInfo } from '@/components/tabs/tabSecurity';
import type { TabNotification } from '@/services/tabs';

const READ_IDS_KEY = 'khiimori.security_alerts.read';
const DISMISSED_IDS_KEY = 'khiimori.security_alerts.dismissed';

function loadIdSet(key: string): Set<string> {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((id): id is string => typeof id === 'string'));
  } catch {
    return new Set();
  }
}

function saveIdSet(key: string, ids: Set<string>): void {
  localStorage.setItem(key, JSON.stringify([...ids]));
}

export function getSecurityAlertReadIds(): Set<string> {
  return loadIdSet(READ_IDS_KEY);
}

export function getSecurityAlertDismissedIds(): Set<string> {
  return loadIdSet(DISMISSED_IDS_KEY);
}

export function markSecurityAlertRead(id: string): void {
  const ids = getSecurityAlertReadIds();
  ids.add(id);
  saveIdSet(READ_IDS_KEY, ids);
}

export function markAllSecurityAlertsRead(alertIds: string[]): void {
  const ids = getSecurityAlertReadIds();
  for (const id of alertIds) ids.add(id);
  saveIdSet(READ_IDS_KEY, ids);
}

export function dismissSecurityAlert(id: string): void {
  const ids = getSecurityAlertDismissedIds();
  ids.add(id);
  saveIdSet(DISMISSED_IDS_KEY, ids);
}

export function isSecurityAlertId(id: string): boolean {
  return id.startsWith('sec-');
}

export interface TabForSecurityAlert {
  id: string;
  title?: string;
  url: string;
  updated_at?: string;
  created_at?: string;
}

// Findings that are worth an alert. Low-severity findings are shown in the
// security table but would make the alerts feed noisy, except plain HTTP.
function isAlertable(type: string, severity: string): boolean {
  return severity !== 'low' || type === 'insecure_connection';
}

/**
 * Build security notifications from backend scan results (see
 * scanTabsSecurity). Tabs without a scan result produce no alerts.
 */
export function buildSecurityAlertsFromTabs(
  tabs: TabForSecurityAlert[],
  security: Map<string, TabSecurityInfo>,
  opts?: { readIds?: Set<string>; dismissedIds?: Set<string> },
): TabNotification[] {
  const readIds = opts?.readIds ?? getSecurityAlertReadIds();
  const dismissedIds = opts?.dismissedIds ?? getSecurityAlertDismissedIds();
  const alerts: TabNotification[] = [];

  for (const tab of tabs) {
    const result = security.get(tab.id);
    if (!result || result.status === 'unscanned') continue;
    const label = tab.title?.trim() || tab.url;
    const createdAt = tab.updated_at ?? tab.created_at ?? new Date().toISOString();

    for (const finding of result.findings) {
      if (!isAlertable(finding.type, finding.severity)) continue;
      const id = `sec-${tab.id}-${finding.type}`;
      if (dismissedIds.has(id)) continue;
      alerts.push({
        id,
        type: finding.severity === 'high' ? 'error' : 'warning',
        title: finding.description,
        message: `${label} — security score ${result.score ?? '—'}/100 (URL check)`,
        tab: label,
        tabId: tab.id,
        createdAt,
        read: readIds.has(id),
      });
    }
  }

  return alerts.sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));
}
